import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { AnimatePresence, motion } from "framer-motion";
import {
  useCreateCompanyMutation,
  useGetCompanyQuery,
  useGetIndustriesQuery,
  useGetProvincesQuery,
  useLazyGetUserOrganizationsQuery,
  useUpdateCompanyMutation,
  useVerifyCompanyMutation,
} from "reduxFiles/api/companyApi";
import RelationsSection from "./sections/RelationsSection";
import { documentsSchema } from "./sections/documents.schema";
import CompanyStepper from "./CompanyStepper";
import StepNavigation from "./StepNavigation";
import { useTranslation } from "react-i18next";
import { buildCompanyPayload } from "./utils/buildPayload";
import DynamicSection from "./DynamicSection";
import DraftRecoveryModal from "./DraftRecoveryModal";
import { basicInfoSchema } from "./sections/basicInfo.schema";
import { businessSchema } from "./sections/business.shema";
import { useLazyGetUsersQuery } from "reduxFiles/api/userApi";
import HeaderBox from "components/navbar/HeaderBox";
import { useScope } from "hooks/useScope";
import { useDependentSelect } from "hooks/useDependentSelect";
import useCompanyDraft from "./utils/useCompanyDraft";
import { useAppDispatch, useAppSelector } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import { Company } from "types/companyTypes";

export default function CompanyModule({
  mode,
}: {
  mode: "create" | "edit" | "view";
}) {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.auth.user);
  const scope = useScope();
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const isViewMode = mode === "view";
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const isRTL = i18n.language === "fa" || i18n.language === "ar";

  const isCompanyScope = useMemo(() => {
    return scope === "user";
  }, [scope]);

  const defaultValues: Partial<Company> = useMemo(
    () => ({
      name: "",
      nationalId: "",
      logo: null,
      provinceId: null,
      ecosystemId: null,
      userId: null,
      organizationId: null,
      industrialId: null,
      industrialOther: "",
      companyStage: null,
      companyType: undefined,
      companyTypeOther: "",
      foundationYear: "",
      licenseNumber: null,
      licenseDate: "",
      activityType: undefined,
      activityDescription: "",
      totalStaffCount: undefined,
      isKnowledgeBased: false,
      knowledgeBasedYear: null,
      isTechnologist: false,
      technologistLicenseNumber: undefined,
      technologistLicenseDate: "",
      hasExport: false,
      exportAmount: null,
      exportVolume: null,
      isListed: false,
      industrySectorId: undefined,
      industrySectorOther: "",
      products: [],
      suppliers: [],
      customers: [],
      people: [],
      processSteps: [],
      responding: [],
      documents: [],
      organizationChartImage: [],
      meta: {},
    }),
    []
  );

  const form = useForm({
    mode: "onChange",
    defaultValues,
  });

  const [showDraftModal, setShowDraftModal] = useState(false);

  const { saveStatus, clearDraft } = useCompanyDraft({
    storageKey: `${user.id}-company-create-new`,
    watch: form.watch,
    reset: form.reset,
    step,
    setStep,
    enabled: isCreateMode && !isViewMode && isCompanyScope,
  });

  useEffect(() => {
    if (isCreateMode) {
      form.reset(defaultValues);
      setStep(1);
      setIsNationalIdLocked(false);
    }
  }, [isCreateMode, form, defaultValues]);

  useEffect(() => {
    if (isCreateMode && !isViewMode && isCompanyScope) {
      const draftKey = `${user.id}-company-create-new`;
      const hasDraft = localStorage.getItem(draftKey);
      if (hasDraft) {
        try {
          const parsed = JSON.parse(hasDraft);
          const hasValues = Object.values(parsed.values || {}).some((val) => {
            if (Array.isArray(val)) return val.length > 0;
            if (val === null || val === undefined || val === "") return false;
            if (typeof val === "object") {
              return Object.values(val).some(
                (v) => v !== null && v !== undefined && v !== ""
              );
            }
            return true;
          });

          if (hasValues) {
            setShowDraftModal(true);
          } else {
            localStorage.removeItem(`${user.id}-company-create-new`);
          }
        } catch (error) {
          console.error("Error checking draft:", error);
          localStorage.removeItem(`${user.id}-company-create-new`);
        }
      }
    }
  }, [id, isCreateMode, isViewMode, isCompanyScope, user]);

  const [triggerUsers] = useLazyGetUsersQuery();
  const searchUsers = useCallback(
    async (text: string) => {
      try {
        const res = await triggerUsers({
          page: 1,
          limit: 50,
          filters: { search: text },
        }).unwrap();

        return res.items.map((u) => ({
          label: u.fullname + " " + u.mobile || "-",
          value: u.id,
        }));
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    [triggerUsers]
  );

  const selectedProvinceId = useWatch({
    control: form.control,
    name: "provinceId",
  });

  const [triggerOrganizations] = useLazyGetUserOrganizationsQuery();

  useEffect(() => {
    form.setValue("organizationId", null);
    form.setValue("industrialId", null);
  }, [selectedProvinceId, form]);

  const fetchIndustrialOrganizationsByProvince = useCallback(
    async (provinceId: number) => {
      try {
        const res = await triggerOrganizations({
          page: 1,
          limit: 100,
          filters: {
            provinceId: Number(provinceId),
            industrial: true,
          },
        }).unwrap();

        return res.items.map((org) => ({
          label: org.name,
          value: org.id,
        }));
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    [triggerOrganizations]
  );

  const fetchTechnologyOrganizationsByProvince = useCallback(
    async (provinceId: number) => {
      try {
        const res = await triggerOrganizations({
          page: 1,
          limit: 100,
          filters: {
            provinceId: Number(provinceId),
            industrial: false,
          },
        }).unwrap();

        return res.items.map((org) => ({
          label: org.name,
          value: org.id,
        }));
      } catch (err) {
        console.error(err);
        return [];
      }
    },
    [triggerOrganizations]
  );

  const { options: industrialOptions } = useDependentSelect({
    form,
    dependOn: "provinceId",
    targetField: "industrialId",
    fetchData: fetchIndustrialOrganizationsByProvince,
    clearTargetOnDependencyChange: true,
  });

  const { options: organizationOptions } = useDependentSelect({
    form,
    dependOn: "provinceId",
    targetField: "organizationId",
    fetchData: fetchTechnologyOrganizationsByProvince,
    clearTargetOnDependencyChange: true,
  });

  const [isNationalIdLocked, setIsNationalIdLocked] = useState(false);

  const [verifyCompany] = useVerifyCompanyMutation();

  const validateCompany = useCallback(
    async (nationalId: string, organizationId: number) => {
      try {
        const result = await verifyCompany({
          nationalId,
          organizationId,
        }).unwrap();

        if (result.data?.success === true) {
          setIsNationalIdLocked(true);
          return true;
        }
      } catch (err) {
        console.error(err);

        return false;
      }
    },
    [verifyCompany]
  );

  const handleBeforeSelectOrganization = useCallback(
    async (value: any, formValues: any) => {
      if (isViewMode) return false;

      if (!value) return true;

      const nationalId = formValues.nationalId;

      if (!nationalId) {
        dispatch(
          addNotification({
            type: "error",
            message: t("company.verification.needNationalIdFirst"),
            duration: 5000,
          })
        );
        return false;
      }

      const currentOrgId = form.getValues("organizationId");
      if (currentOrgId === value && isNationalIdLocked) {
        return true;
      }

      const isValid = await validateCompany(nationalId, value);

      if (isValid) {
        setIsNationalIdLocked(true);
      }

      return isValid;
    },
    [isViewMode, form, isNationalIdLocked, validateCompany, dispatch, t]
  );

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "nationalId") {
        form.setValue("organizationId", null);
        setIsNationalIdLocked(false);
      }

      if (name === "provinceId") {
        setIsNationalIdLocked(false);
      }

      if (name === "organizationId") {
        if (!value.organizationId) {
          setIsNationalIdLocked(false);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const { data: provinces } = useGetProvincesQuery();
  const { data: industries } = useGetIndustriesQuery();

  const provinceOptions = useMemo(
    () =>
      provinces?.map((opt) => ({
        label: opt.name,
        value: opt.id,
      })) ?? [],
    [provinces]
  );

  const industryOptions = useMemo(
    () =>
      industries?.map((opt) => ({
        label: `${opt.index} - ${t(`industrySectors.${opt.key}`)}`,
        value: opt.id,
        key: opt.key,
      })) ?? [],
    [industries, t]
  );

  const {
    data,
    refetch,
    isLoading: isCompanyLoading,
    isFetching: isCompanyFetching,
  } = useGetCompanyQuery(
    {
      id: Number(id),
      scope,
    },
    {
      skip: isCreateMode || !id,
    }
  );

  const isCompanyDataReady =
    isCreateMode || (!!data && !isCompanyLoading && !isCompanyFetching);

  const [createCompany, createState] = useCreateCompanyMutation();
  const [updateCompany, updateState] = useUpdateCompanyMutation();

  const isLoading = createState.isLoading || updateState.isLoading;

  useEffect(() => {
    if (data && !isCreateMode) {
      const payload = buildCompanyPayload(data, mode);
      form.reset(payload);

      const provinceId = data.provinceId || data.province?.id;
      if (provinceId) {
        fetchTechnologyOrganizationsByProvince(provinceId).then((options) => {
          const orgId = data.organization?.id || data.organizationId;
          if (orgId && options.some((opt) => opt.value === orgId)) {
            form.setValue("organizationId", orgId);
          }
        });

        fetchIndustrialOrganizationsByProvince(provinceId).then((options) => {
          const industrialId = data.industrial?.id || data.industrialId;
          if (
            industrialId &&
            options.some((opt) => opt.value === industrialId)
          ) {
            form.setValue("industrialId", industrialId);
          }
        });
      }
    }
  }, [
    data,
    isCreateMode,
    form,
    mode,
    fetchTechnologyOrganizationsByProvince,
    fetchIndustrialOrganizationsByProvince,
  ]);

  const onSubmit = useCallback(
    async (values: any) => {
      if (isViewMode) return;

      const payload = buildCompanyPayload(values, mode);

      try {
        if (isCreateMode) {
          await createCompany({
            scope,
            body: payload,
          }).unwrap();

          clearDraft();

          navigate(`/${scope === "user" ? "company" : scope}/companies/list`);
        } else if (isEditMode && id) {
          await updateCompany({
            id: Number(id),
            scope,
            body: payload,
          }).unwrap();
          refetch();
        }
      } catch (err: any) {
        console.error(err);
      }
    },
    [
      isViewMode,
      mode,
      isCreateMode,
      isEditMode,
      id,
      createCompany,
      clearDraft,
      scope,
      navigate,
      updateCompany,
      refetch,
    ]
  );

  const getBasicInfoSchema = useCallback(() => {
    const schema = basicInfoSchema(
      t,
      scope,
      organizationOptions,
      industrialOptions,
      isNationalIdLocked,
      provinceOptions,
      searchUsers,
      isViewMode,
      data
    );

    const orgField = schema.find((field) => field.name === "organizationId");
    if (orgField) {
      orgField.onBeforeChange = handleBeforeSelectOrganization;
    }

    if ((isNationalIdLocked && isEditMode) || isViewMode) {
      const nationalIdField = schema.find(
        (field) => field.name === "nationalId"
      );
      if (nationalIdField) {
        nationalIdField.disabled = true;
      }
    }

    if (isViewMode) {
      schema.forEach((field) => {
        field.disabled = true;
      });
    }

    return schema;
  }, [
    t,
    scope,
    organizationOptions,
    industrialOptions,
    isNationalIdLocked,
    provinceOptions,
    searchUsers,
    isViewMode,
    data,
    isEditMode,
    handleBeforeSelectOrganization,
  ]);

  const renderStep = useMemo(() => {
    const stepConfig = {
      1: {
        schema: getBasicInfoSchema(),
        title: t("company.companyInfo"),
        desc: t("company.companyInfoDesc"),
      },
      2: {
        schema: businessSchema(t, industryOptions, isViewMode),
        title: t("company.businessInfo"),
        desc: t("company.companyInfoDesc"),
      },
      3: {
        component: (
          <RelationsSection key={step} scope={scope} isViewMode={isViewMode} />
        ),
      },
      4: {
        schema: documentsSchema(t, isViewMode),
        title: t("company.documentsInfo"),
        desc: t("company.documentsInfoDesc"),
      },
    };

    const currentConfig = stepConfig[step as keyof typeof stepConfig];

    if ("component" in currentConfig) {
      return currentConfig.component;
    }

    return (
      <DynamicSection
        key={isCreateMode ? "create" : String(data?.id ?? "loading")}
        title={currentConfig.title}
        description={currentConfig.desc}
        fields={currentConfig.schema}
        scope={scope}
        isViewMode={isViewMode}
      />
    );
  }, [
    getBasicInfoSchema,
    t,
    industryOptions,
    isViewMode,
    step,
    scope,
    isCreateMode,
    data?.id,
  ]);

  const handleStepChange = useCallback((newStep: number) => {
    setStep(newStep);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div
      className={`from-slate-50 dark:from-gray-950 dark:to-gray-950 min-h-screen bg-gradient-to-br via-white to-indigo-50 dark:via-gray-900 ${
        isRTL ? "rtl" : "ltr"
      }`}
    >
      {!isCreateMode && (
        <HeaderBox
          title={t("company.companyListBack")}
          showBack
          refreshBtn={refetch}
        />
      )}

      <div className="container mx-auto max-w-7xl px-2 py-3 sm:px-4 sm:py-6">
        <FormProvider {...form}>
          <div className="mb-4">
            <div className="overflow-x-auto pb-2">
              <div className="min-w-[300px]">
                <CompanyStepper
                  currentStep={step}
                  onStepChange={handleStepChange}
                  mode={mode}
                  steps={[
                    { id: 1, title: t("company.companyModule.steps.step1") },
                    { id: 2, title: t("company.companyModule.steps.step2") },
                    { id: 3, title: t("company.companyModule.steps.step3") },
                    { id: 4, title: t("company.companyModule.steps.step4") },
                  ]}
                />
              </div>
            </div>
            <div className="px-1 sm:px-4">
              <StepNavigation
                step={step}
                setStep={handleStepChange}
                totalSteps={4}
                loading={isLoading}
                onSubmit={() => form.handleSubmit(onSubmit)()}
                mode={mode}
                isTopNav={true}
              />
            </div>
          </div>

          <motion.div
            layout
            className={`rounded-xl border border-white/20 bg-white/80 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-gray-900/60 sm:rounded-2xl ${
              isViewMode ? "opacity-90" : ""
            }`}
          >
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="p-4 sm:p-6 md:p-8">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {!isCompanyDataReady ? (
                      <div className="flex items-center justify-center py-20">
                        <div className="loader w-6 text-brand-500" />
                      </div>
                    ) : (
                      renderStep
                    )}
                  </motion.div>
                </AnimatePresence>
                <div className="mt-4 px-1 sm:px-4">
                  <StepNavigation
                    step={step}
                    setStep={handleStepChange}
                    totalSteps={4}
                    loading={isLoading}
                    onSubmit={() => form.handleSubmit(onSubmit)()}
                    mode={mode}
                    hideSubmit={true}
                    isTopNav={false}
                  />
                </div>
              </div>
            </form>
          </motion.div>

          {isCompanyScope && isCreateMode && saveStatus === "saving" && (
            <div className="fixed bottom-4 end-4 rounded-lg bg-blue-500 px-3 py-1 text-sm text-white shadow-lg">
              {t("company.isSaving")}
            </div>
          )}
          {isCompanyScope && isCreateMode && saveStatus === "saved" && (
            <div className="fixed bottom-4 end-4 rounded-lg bg-green-500 px-3 py-1 text-sm text-white shadow-lg">
              {t("company.isDone")}
            </div>
          )}

          {isCompanyScope && isCreateMode && !isViewMode && (
            <DraftRecoveryModal
              open={showDraftModal}
              onRestore={() => {
                setShowDraftModal(false);
              }}
              onDiscard={() => {
                localStorage.removeItem(`${user.id}-company-create-new`);
                window.location.reload();
              }}
            />
          )}
        </FormProvider>
      </div>
    </div>
  );
}
